import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { AuthService } from '@core/services/auth.service';
import { AuthActions } from './auth.actions';

/**
 * Shape of the persisted auth slice.
 *
 * `status` surfaces the current async step to the UI, while `lastError`
 * carries the raw server status code for the most recent failed attempt
 * so that login / register / reset-password / resend-activation pages
 * can each render their own contextual error messages.
 */
export interface AuthStateModel {
  user_id: number | null;
  api_id: string | null;
  email: string | null;
  status:
    | 'idle'
    | 'submitting_login'
    | 'submitting_register'
    | 'submitting_reset'
    | 'submitting_resend'
    | 'logged_in';
  lastError: string | null;
  /**
   * One-shot flag set true after a successful register / reset / resend
   * so routed pages can show a confirmation message and navigate back to
   * login. UI components should ClearError after consuming it.
   */
  lastSuccess: 'register' | 'reset' | 'resend' | null;
}

export const INITIAL_AUTH_STATE: AuthStateModel = {
  user_id: null,
  api_id: null,
  email: null,
  status: 'idle',
  lastError: null,
  lastSuccess: null,
};

@State<AuthStateModel>({
  name: 'auth',
  defaults: INITIAL_AUTH_STATE,
})
@Injectable()
export class AuthState {
  private readonly authService = inject(AuthService);

  // ------------------------------------------------------------------
  // Selectors
  // ------------------------------------------------------------------

  /**
   * Returns the entire AuthStateModel. Used by pages that need to react
   * to multiple fields at once (status, lastError, lastSuccess) — they
   * can subscribe to this instead of combining multiple @Selector-based
   * observables. NGXS 18 tightened Store.select()'s typing such that
   * passing the state class directly no longer infers the model type,
   * so this named selector is the clean replacement.
   */
  @Selector()
  static whole(state: AuthStateModel): AuthStateModel {
    return state;
  }

  @Selector()
  static isLoggedIn(state: AuthStateModel): boolean {
    return !!state.api_id && !!state.user_id;
  }

  @Selector()
  static apiId(state: AuthStateModel): string | null {
    return state.api_id;
  }

  @Selector()
  static userId(state: AuthStateModel): number | null {
    return state.user_id;
  }

  @Selector()
  static email(state: AuthStateModel): string | null {
    return state.email;
  }

  @Selector()
  static status(state: AuthStateModel): AuthStateModel['status'] {
    return state.status;
  }

  @Selector()
  static lastError(state: AuthStateModel): string | null {
    return state.lastError;
  }

  @Selector()
  static lastSuccess(state: AuthStateModel): AuthStateModel['lastSuccess'] {
    return state.lastSuccess;
  }

  // ------------------------------------------------------------------
  // Login
  // ------------------------------------------------------------------

  @Action(AuthActions.SubmitLogin)
  submitLogin(ctx: StateContext<AuthStateModel>, { email, password }: AuthActions.SubmitLogin) {
    ctx.patchState({ status: 'submitting_login', lastError: null, lastSuccess: null });

    return this.authService.login(email, password).pipe(
      tap((result) => {
        if (result.ok && result.api_id && result.user_id !== undefined) {
          ctx.dispatch(
            new AuthActions.LoginSuccess(result.user_id, result.api_id, email),
          );
        } else {
          ctx.dispatch(
            new AuthActions.LoginFailure(
              result.ok ? 'bad_data' : result.status || 'bad_data',
            ),
          );
        }
      }),
    );
  }

  @Action(AuthActions.LoginSuccess)
  loginSuccess(
    ctx: StateContext<AuthStateModel>,
    { user_id, api_id, email }: AuthActions.LoginSuccess,
  ) {
    ctx.patchState({
      user_id,
      api_id,
      email,
      status: 'logged_in',
      lastError: null,
      lastSuccess: null,
    });
  }

  @Action(AuthActions.LoginFailure)
  loginFailure(ctx: StateContext<AuthStateModel>, { status }: AuthActions.LoginFailure) {
    ctx.patchState({ status: 'idle', lastError: status });
  }

  // ------------------------------------------------------------------
  // Register
  // ------------------------------------------------------------------

  @Action(AuthActions.SubmitRegister)
  submitRegister(
    ctx: StateContext<AuthStateModel>,
    { email, password }: AuthActions.SubmitRegister,
  ) {
    ctx.patchState({ status: 'submitting_register', lastError: null, lastSuccess: null });

    return this.authService.register(email, password).pipe(
      tap((result) => {
        if (result.ok) {
          ctx.dispatch(new AuthActions.RegisterSuccess());
        } else {
          ctx.dispatch(new AuthActions.RegisterFailure(result.status || 'bad_data'));
        }
      }),
    );
  }

  @Action(AuthActions.RegisterSuccess)
  registerSuccess(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ status: 'idle', lastError: null, lastSuccess: 'register' });
  }

  @Action(AuthActions.RegisterFailure)
  registerFailure(ctx: StateContext<AuthStateModel>, { status }: AuthActions.RegisterFailure) {
    ctx.patchState({ status: 'idle', lastError: status });
  }

  // ------------------------------------------------------------------
  // Reset Password
  // ------------------------------------------------------------------

  @Action(AuthActions.SubmitResetPassword)
  submitResetPassword(
    ctx: StateContext<AuthStateModel>,
    { email, newPassword }: AuthActions.SubmitResetPassword,
  ) {
    ctx.patchState({ status: 'submitting_reset', lastError: null, lastSuccess: null });

    return this.authService.resetPassword(email, newPassword).pipe(
      tap((result) => {
        if (result.ok) {
          ctx.dispatch(new AuthActions.ResetPasswordSuccess());
        } else {
          ctx.dispatch(new AuthActions.ResetPasswordFailure(result.status || 'bad_data'));
        }
      }),
    );
  }

  @Action(AuthActions.ResetPasswordSuccess)
  resetPasswordSuccess(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ status: 'idle', lastError: null, lastSuccess: 'reset' });
  }

  @Action(AuthActions.ResetPasswordFailure)
  resetPasswordFailure(
    ctx: StateContext<AuthStateModel>,
    { status }: AuthActions.ResetPasswordFailure,
  ) {
    ctx.patchState({ status: 'idle', lastError: status });
  }

  // ------------------------------------------------------------------
  // Resend Activation
  // ------------------------------------------------------------------

  @Action(AuthActions.SubmitResendActivation)
  submitResendActivation(
    ctx: StateContext<AuthStateModel>,
    { email, password }: AuthActions.SubmitResendActivation,
  ) {
    ctx.patchState({ status: 'submitting_resend', lastError: null, lastSuccess: null });

    return this.authService.resendActivation(email, password).pipe(
      tap((result) => {
        if (result.ok) {
          ctx.dispatch(new AuthActions.ResendActivationSuccess());
        } else {
          ctx.dispatch(new AuthActions.ResendActivationFailure(result.status || 'bad_data'));
        }
      }),
    );
  }

  @Action(AuthActions.ResendActivationSuccess)
  resendActivationSuccess(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ status: 'idle', lastError: null, lastSuccess: 'resend' });
  }

  @Action(AuthActions.ResendActivationFailure)
  resendActivationFailure(
    ctx: StateContext<AuthStateModel>,
    { status }: AuthActions.ResendActivationFailure,
  ) {
    ctx.patchState({ status: 'idle', lastError: status });
  }

  // ------------------------------------------------------------------
  // Housekeeping
  // ------------------------------------------------------------------

  @Action(AuthActions.Logout)
  logout(ctx: StateContext<AuthStateModel>) {
    // Reset to pristine initial state. Persistence plugin writes null
    // values back so the user is fully logged out across reloads.
    ctx.setState(INITIAL_AUTH_STATE);
  }

  @Action(AuthActions.ClearError)
  clearError(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ lastError: null, lastSuccess: null });
  }
}
